import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { login } from '../store/authSlice';
import { toast } from 'react-toastify';
import api from '../services/api';
import { FiEye, FiEyeOff, FiArrowLeft, FiPhone, FiMail } from 'react-icons/fi';
import './Login.css';

export default function Login() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const isSignup = searchParams.get('mode') === 'signup';

  const [mode, setMode] = useState(isSignup ? 'signup' : 'login');
  const [loginMethod, setLoginMethod] = useState('email'); // 'email' | 'phone'
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (mode === 'login') {
      if (loginMethod === 'email') {
        if (!form.email) errs.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Invalid email';
      } else {
        if (!form.phone) errs.phone = 'Phone number is required';
        else if (!/^\d{10}$/.test(form.phone)) errs.phone = 'Enter a valid 10-digit number';
      }
      if (!form.password) errs.password = 'Password is required';
    } else {
      if (!form.name) errs.name = 'Name is required';
      if (!form.email) errs.email = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Invalid email';
      if (!form.phone) errs.phone = 'Phone is required';
      else if (!/^\d{10}$/.test(form.phone)) errs.phone = 'Enter a valid 10-digit number';
      if (!form.password) errs.password = 'Password is required';
      else if (form.password.length < 6) errs.password = 'Minimum 6 characters';
      if (form.password !== form.confirmPassword) errs.confirmPassword = 'Passwords do not match';
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      const endpoint = mode === 'login' ? '/api/auth/login' : '/api/auth/signup';
      const payload = mode === 'login'
        ? (loginMethod === 'email' ? { email: form.email, password: form.password } : { phone: form.phone, password: form.password })
        : form;
      const res = await api.post(endpoint, payload);
      dispatch(login({ user: res.data.user, token: res.data.token }));
      toast.success(mode === 'login' ? 'Welcome back!' : 'Account created!');
      navigate('/');
    } catch {
      if (mode === 'login' && form.email === 'admin@huge.com' && form.password === 'admin123') {
        dispatch(login({ user: { id: 0, name: 'Admin', email: form.email, role: 'ADMIN' }, token: 'demo-admin-token' }));
        toast.success('Welcome, Admin!');
        navigate('/admin');
      } else {
        const name = form.name || (loginMethod === 'phone' ? `User${form.phone?.slice(-4)}` : form.email?.split('@')[0]);
        const demoUser = { id: 1, name, email: form.email || `${form.phone}@huge.com`, role: 'USER' };
        dispatch(login({ user: demoUser, token: 'demo-user-token' }));
        toast.success(mode === 'login' ? 'Welcome back!' : 'Account created!');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const setField = (k, v) => { setForm(p => ({ ...p, [k]: v })); setErrors(p => ({ ...p, [k]: '' })); };

  return (
    <div className="login-page">
      <div className="login-layout">

        {/* Left Banner */}
        <div className="login-banner">
          <div className="login-banner-content">
            <Link to="/" className="login-logo">HUGE</Link>
            <h2>India's Fashion<br />Expert</h2>
            <p>Shop from 5,000+ brands. Best fashion deals every day.</p>
            <ul className="login-perks">
              <li><span className="perk-check">✓</span> 50M+ happy customers</li>
              <li><span className="perk-check">✓</span> Free returns on all orders</li>
              <li><span className="perk-check">✓</span> Exclusive brand deals</li>
              <li><span className="perk-check">✓</span> Latest fashion trends</li>
              <li><span className="perk-check">✓</span> 24/7 customer support</li>
            </ul>
            <div className="login-banner-emojis">
              {['👗','👔','👟','👜','💄'].map((e, i) => (
                <span key={i} className="banner-emoji" style={{ animationDelay: `${i * 0.15}s` }}>{e}</span>
              ))}
            </div>
          </div>
          <div className="banner-orb banner-orb-1" />
          <div className="banner-orb banner-orb-2" />
          <div className="banner-orb banner-orb-3" />
        </div>

        {/* Right Form */}
        <div className="login-form-side">
          <div className="login-form-container">
            <button className="back-btn" onClick={() => navigate(-1)}>
              <FiArrowLeft /> Back
            </button>

            {/* Mode tabs: Login / Sign Up */}
            <div className="login-tabs">
              <button className={mode === 'login' ? 'active' : ''} onClick={() => setMode('login')}>Login</button>
              <button className={mode === 'signup' ? 'active' : ''} onClick={() => setMode('signup')}>Sign Up</button>
            </div>

            {/* Login method switcher (only on login mode) */}
            {mode === 'login' && (
              <div className="login-method-switch">
                <button
                  className={loginMethod === 'email' ? 'method-btn active' : 'method-btn'}
                  onClick={() => setLoginMethod('email')}
                >
                  <FiMail /> Email
                </button>
                <button
                  className={loginMethod === 'phone' ? 'method-btn active' : 'method-btn'}
                  onClick={() => setLoginMethod('phone')}
                >
                  <FiPhone /> Phone
                </button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="login-form">

              {/* Signup fields */}
              {mode === 'signup' && (
                <div className="input-group">
                  <label>Full Name *</label>
                  <input type="text" placeholder="Enter your full name"
                    value={form.name} onChange={e => setField('name', e.target.value)}
                    className={errors.name ? 'error' : ''} />
                  {errors.name && <span className="field-error">{errors.name}</span>}
                </div>
              )}

              {/* Email field — login email mode OR signup */}
              {(mode === 'signup' || (mode === 'login' && loginMethod === 'email')) && (
                <div className="input-group">
                  <label>Email Address *</label>
                  <input type="email" placeholder="Enter your email"
                    value={form.email} onChange={e => setField('email', e.target.value)}
                    className={errors.email ? 'error' : ''} />
                  {errors.email && <span className="field-error">{errors.email}</span>}
                </div>
              )}

              {/* Phone field — login phone mode OR signup */}
              {(mode === 'signup' || (mode === 'login' && loginMethod === 'phone')) && (
                <div className="input-group">
                  <label>{mode === 'signup' ? 'Phone Number *' : 'Mobile Number *'}</label>
                  <div className="phone-input-wrap">
                    <span className="phone-prefix">+91</span>
                    <input type="tel" placeholder="10-digit mobile number"
                      value={form.phone}
                      onChange={e => setField('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                      className={errors.phone ? 'error' : ''} maxLength={10} />
                  </div>
                  {errors.phone && <span className="field-error">{errors.phone}</span>}
                </div>
              )}

              {/* Password */}
              <div className="input-group">
                <label>Password *</label>
                <div className="password-input-wrap">
                  <input type={showPass ? 'text' : 'password'} placeholder="Min 6 characters"
                    value={form.password} onChange={e => setField('password', e.target.value)}
                    className={errors.password ? 'error' : ''} />
                  <button type="button" className="show-pass-btn" onClick={() => setShowPass(!showPass)}>
                    {showPass ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
                {errors.password && <span className="field-error">{errors.password}</span>}
              </div>

              {/* Confirm password on signup */}
              {mode === 'signup' && (
                <div className="input-group">
                  <label>Confirm Password *</label>
                  <input type="password" placeholder="Re-enter your password"
                    value={form.confirmPassword} onChange={e => setField('confirmPassword', e.target.value)}
                    className={errors.confirmPassword ? 'error' : ''} />
                  {errors.confirmPassword && <span className="field-error">{errors.confirmPassword}</span>}
                </div>
              )}

              {mode === 'login' && (
                <div className="forgot-row">
                  <Link to="/forgot-password" className="forgot-link">Forgot Password?</Link>
                </div>
              )}

              <button type="submit" className="login-submit-btn" disabled={loading}>
                <span>{loading ? 'Please wait…' : mode === 'login' ? 'LOGIN' : 'CREATE ACCOUNT'}</span>
              </button>

              {mode === 'login' && (
                <div className="demo-credentials">
                  <p>Admin demo: <strong>admin@huge.com</strong> / <strong>admin123</strong></p>
                  <p>Any other credentials → User login</p>
                </div>
              )}

              {mode === 'login' ? (
                <p className="login-switch">
                  New to HUGE? <button type="button" onClick={() => setMode('signup')}>Create Account</button>
                </p>
              ) : (
                <p className="login-switch">
                  Already have an account? <button type="button" onClick={() => setMode('login')}>Login</button>
                </p>
              )}

              <p className="login-terms">
                By {mode === 'login' ? 'logging in' : 'signing up'}, you agree to our{' '}
                <Link to="/terms">Terms of Use</Link> and <Link to="/privacy">Privacy Policy</Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
